import { Stack, SxProps, Theme } from "@mui/material";
import { SenecButton } from "src/components/button/senec-button/SenecButton";
import { ChevronLeftIcon, ChevronRightIcon } from "src/foundations/icons";
import { Dispatch, ReactElement, SetStateAction } from "react";
import { SenecButtonLevel } from "src/components/button/ButtonUtils";
import { useScreenSize } from "src/hooks/useScreenSize";

export interface StepperButtonProps {
    label?: string | ReactElement;
    handleClick?: () => void;
    level?: SenecButtonLevel;
    sx?: SxProps<Theme>;
    disabled?: boolean;
}

export interface StepperButtons {
    skip?: StepperButtonProps;
    previous?: StepperButtonProps;
    next?: StepperButtonProps;
    start?: StepperButtonProps;
    finish?: StepperButtonProps;
}

export interface StepperProps {
    activeStep: number;
    totalSteps: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    buttons: StepperButtons;
    showSkip?: boolean;
}

export const SenecStepperButtons = ({ activeStep, totalSteps, setActiveStep, buttons, showSkip = false }: StepperProps) => {
    const { isMobile } = useScreenSize();
    const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);
    const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

    const showFinishBtn = buttons.finish && activeStep == totalSteps - 1;

    return (
        <Stack direction={isMobile ? "column" : "row"} spacing={"12px"} width={isMobile ? "100%" : "auto"}>
            {/* SKIP */}
            {buttons.skip && (!showFinishBtn || showSkip) && (
                <SenecButton
                    level={buttons.skip.level ?? "secondary"}
                    size={"small"}
                    onClick={buttons.skip.handleClick}
                    data-testid={"onboarding-dialog-stepper-skip-button"}
                    disabled={buttons.skip.disabled}
                    fullWidth={isMobile}
                    sx={buttons.skip.sx}
                >
                    {buttons.skip.label}
                </SenecButton>
            )}
            {/* START */}
            {buttons.start && activeStep == 0 && totalSteps > 1 && (
                <SenecButton
                    level={buttons.start.level ?? "primary"}
                    size={"small"}
                    onClick={handleNext}
                    data-testid={"onboarding-dialog-stepper-start-button"}
                    disabled={buttons.start.disabled}
                    fullWidth={isMobile}
                    sx={buttons.start.sx}
                >
                    {buttons.start.label}
                </SenecButton>
            )}
            {/* PREVIOUS */}
            {activeStep > 0 && totalSteps > 1 && (
                <SenecButton
                    level={buttons.previous?.level ?? "secondary"}
                    size="small"
                    onClick={buttons.previous?.handleClick ? buttons.previous.handleClick : handleBack}
                    data-testid={"previous-btn"}
                    disabled={buttons.previous?.disabled}
                    fullWidth={isMobile}
                    sx={{ ...getBtnStyle(buttons.previous), ...(buttons.previous?.sx ?? {}) } as SxProps}
                >
                    {buttons.previous?.label ?? <ChevronLeftIcon />}
                </SenecButton>
            )}
            {/* NEXT */}
            {(activeStep != 0 || buttons.start === undefined) && activeStep != totalSteps - 1 && totalSteps > 0 && (
                <SenecButton
                    size="small"
                    onClick={buttons.next?.handleClick ? buttons.next.handleClick : handleNext}
                    level={buttons.next?.level ?? "primary"}
                    disabled={activeStep === totalSteps - 1 || buttons.next?.disabled}
                    data-testid={"next-btn"}
                    fullWidth={isMobile}
                    sx={{ ...getBtnStyle(buttons.next), ...(buttons.next?.sx ?? {}) } as SxProps}
                >
                    {buttons.next?.label ?? <ChevronRightIcon />}
                </SenecButton>
            )}
            {/* FINISH */}
            {showFinishBtn && buttons.finish && activeStep == totalSteps - 1 && (
                <SenecButton
                    level={buttons.finish.level ?? "primary"}
                    size={"small"}
                    onClick={buttons.finish.handleClick}
                    data-testid={"onboarding-dialog-stepper-finish-button"}
                    fullWidth={isMobile}
                    sx={buttons.finish.sx}
                    disabled={buttons.finish.disabled}
                >
                    {buttons.finish.label}
                </SenecButton>
            )}
        </Stack>
    );
};

export const iconButtonWidthSmall = "40px";

const getBtnStyle = (button?: StepperButtonProps) => {
    const roundStyle = { padding: 0, minWidth: iconButtonWidthSmall };
    if (!button) return roundStyle;

    if (button.label) return { ...button.sx };

    return { ...roundStyle, ...button.sx };
};
